import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserDailyStatisticsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function UserDailyStatisticsSubTab() {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingStatistics, setEditingStatistics] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    telegram_id: '',
    consecutive_days: 0,
    active_dates: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/user-daily-statistics`);
      setStatistics(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        telegram_id: parseInt(formData.telegram_id),
        consecutive_days: parseInt(formData.consecutive_days) || 0,
        active_dates: parseActiveDates(formData.active_dates)
      };
      await axios.post(`${API_URL}/api/user-daily-statistics`, payload);
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Ошибка при создании статистики: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdate = async (id) => {
    try {
      const payload = {
        telegram_id: parseInt(formData.telegram_id),
        consecutive_days: parseInt(formData.consecutive_days) || 0,
        active_dates: parseActiveDates(formData.active_dates)
      };
      await axios.put(`${API_URL}/api/user-daily-statistics/${id}`, payload);
      setEditingStatistics(null);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Ошибка при обновлении статистики: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту статистику?')) {
      try {
        await axios.delete(`${API_URL}/api/user-daily-statistics/${id}`);
        fetchData();
      } catch (err) {
        setError('Ошибка при удалении статистики: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleEdit = (stat) => {
    setEditingStatistics(stat.id);
    setFormData({
      telegram_id: stat.telegram_id.toString(),
      consecutive_days: stat.consecutive_days.toString(),
      active_dates: formatActiveDates(stat.active_dates)
    });
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      telegram_id: '',
      consecutive_days: 0,
      active_dates: ''
    });
    setEditingStatistics(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingStatistics(null);
    resetForm();
  };

  const parseActiveDates = (datesString) => {
    if (!datesString || datesString.trim() === '') {
      return [];
    }
    // Разбиваем строку по запятым, пробелам или переносам строк
    return datesString
      .split(/[,\n\r]+/)
      .map(date => date.trim())
      .filter(date => date.length > 0);
  };

  const formatActiveDates = (datesArray) => {
    if (!datesArray || !Array.isArray(datesArray) || datesArray.length === 0) {
      return '';
    }
    return datesArray.join(', ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="user-daily-statistics-sub-tab">
      <div className="tab-header">
        <h2>Ежедневная статистика пользователей</h2>
        <button className="btn btn-primary" onClick={() => {
          resetForm();
          setShowCreateForm(true);
          setEditingStatistics(null);
        }}>
          + Создать статистику
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {(showCreateForm || editingStatistics) && (
        <div className="statistics-form">
          <h3>{editingStatistics ? 'Редактировать статистику' : 'Создать статистику'}</h3>
          <div className="form-group">
            <label>Telegram ID *:</label>
            <input
              type="number"
              value={formData.telegram_id}
              onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
              required
              min="1"
              placeholder="Введите Telegram ID"
              disabled={editingStatistics !== null}
            />
          </div>
          <div className="form-group">
            <label>Дней подряд активен:</label>
            <input
              type="number"
              value={formData.consecutive_days}
              onChange={(e) => setFormData({ ...formData, consecutive_days: e.target.value })}
              min="0"
              placeholder="0"
            />
          </div>
          <div className="form-group">
            <label>Активные даты (формат: YYYY-MM-DD, через запятую):</label>
            <textarea
              value={formData.active_dates}
              onChange={(e) => setFormData({ ...formData, active_dates: e.target.value })}
              placeholder="2026-01-20, 2026-01-19, 2026-01-18"
              rows="4"
            />
            <small className="form-hint">
              Введите даты в формате YYYY-MM-DD, разделенные запятыми или переносами строк
            </small>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={editingStatistics ? () => handleUpdate(editingStatistics) : handleCreate}>
              {editingStatistics ? 'Сохранить' : 'Создать'}
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Отмена
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Telegram ID</th>
              <th>Дней подряд</th>
              <th>Активные даты</th>
              <th>Создано</th>
              <th>Обновлено</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {statistics.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Нет записей статистики. Создайте первую запись.
                </td>
              </tr>
            ) : (
              statistics.map((stat) => (
                <tr key={stat.id}>
                  <td>{stat.id}</td>
                  <td>{stat.telegram_id}</td>
                  <td className="consecutive-days-value">{stat.consecutive_days}</td>
                  <td className="active-dates-cell">
                    {stat.active_dates && Array.isArray(stat.active_dates) && stat.active_dates.length > 0 ? (
                      <div className="active-dates-list">
                        {stat.active_dates.slice(0, 3).map((date, idx) => (
                          <span key={idx} className="date-badge">{date}</span>
                        ))}
                        {stat.active_dates.length > 3 && (
                          <span className="date-badge more">+{stat.active_dates.length - 3}</span>
                        )}
                      </div>
                    ) : (
                      <span className="no-dates">Нет дат</span>
                    )}
                  </td>
                  <td>{formatDate(stat.created_at)}</td>
                  <td>{formatDate(stat.updated_at)}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(stat)}
                      title="Редактировать"
                    >
                      ✎
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(stat.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserDailyStatisticsSubTab;
